import { Grid, SxProps, Theme, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getDateString } from "../common/RoundHelper";
import { PoolXNumberFormat } from "../common/CurrencyFormatter";
import { Syndicate } from "../models/syndicate";
import { ShallowRound } from "../models/round";
import { DbetEightIcon, DbetEightLadder, DbetSevenIcon, DbetSixIcon, DbetHockeyNineLogo, DbetThirteenIcon, DbetThirteenLadder, DbetTwelveIcon, DbetTwelveLadder } from "../images/dbeticons";
import roundTypeBg from '../images/dbet_round_type_background.png';
import TerminalSyndicateCoupon from './terminalsyndicatecoupon';
import useDetailRound from "../rounds/useDetailRound";
import useSyndicate from "../syndicates/useSyndicate";
import { getJackpot } from "../common/roundhelpers";

function TerminalSyndicateCarousel({ slimSyndicate, round }: { slimSyndicate: Syndicate, round: ShallowRound }) {
    const { round: detailedRound } = useDetailRound(slimSyndicate.roundId);
    const { totalAmountPerShare } = slimSyndicate || {};
    const { amount, currency } = totalAmountPerShare || {};
    const syndicateResponse = useSyndicate(slimSyndicate.id);
    let syndicate = syndicateResponse.syndicate;

    const { t } = useTranslation();
    const dateString = getDateString(round, t);

    let locale = t('locale');
    const nf = PoolXNumberFormat(locale, { style: 'currency', currency: currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const nf2 = PoolXNumberFormat(locale, { style: 'currency', currency: currency, minimumFractionDigits: 0, maximumFractionDigits: 0 });
    let formattedStakePerShare = nf.format(amount);

    let jackpot = getJackpot(currency, detailedRound);

    if (syndicate === undefined || (syndicate.coupon === undefined && !syndicate.couponHidden)) {
        return (<></>);
    }

    const syndicateName = syndicate.name.split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');

    const smallText: SxProps<Theme> = {
        fontSize: "16px",
        lineHeight: "120%",
        color: "#65ABE8",
        fontWeight: "500",
        fontFamily: 'Roboto',
        textDecoration: 'none',
    };

    const smallTitleUpper: SxProps<Theme> = {
        fontSize: "24px",
        color: '#C9F0FF',
        fontWeight: "700",
        fontFamily: 'Roboto',
        paddingLeft: '10px',
        paddingRight: '10px',
        textWrap: 'wrap',
        textAlign: 'center',
        textTransform: 'capitalize'
    };

    const stakeStyle: SxProps<Theme> = {
        fontSize: "28px",
        color: '#FFFFFF',
        fontWeight: "700",
        fontFamily: 'Roboto',
        textTransform: 'uppercase',
        textWrap: 'nowrap',
        textAlign: 'left'
    };

    let gradient = `url(${roundTypeBg})`;
    let numberIcon = (
        <Grid container direction="row" alignItems="center">
            <Grid sx={{ marginRight: "3.5px", paddingLeft: "39px" }}>
                {DbetEightIcon(50, 59)}
            </Grid>
            {DbetEightLadder(25, 20)}
        </Grid>
    );

    if (round.name.indexOf("Survivor6") > -1) {
        numberIcon = (
            <Grid container direction="row" alignItems="center">
                <Grid sx={{ marginRight: "3.5px", paddingLeft: "39px" }}>
                    {DbetSixIcon(50, 59)}
                </Grid>
                {DbetEightLadder(25, 20)}
            </Grid>
        );
    }

    if (round.name.indexOf("Hockey7") > -1) {
        numberIcon = (
            <Grid container direction="row" alignItems="center">
                <Grid sx={{ marginRight: "3.5px", paddingLeft: "38px" }}>
                    {DbetSevenIcon(50, 59)}
                </Grid>
                {DbetEightLadder(25, 20)}
            </Grid>
        );
    }

    if (round.name.indexOf("Hockey9") > -1) {
        numberIcon = (
            <Grid container alignItems="center" justifyContent="center">
                {DbetHockeyNineLogo(15, 131)}
            </Grid>
        );
    }

    if (round.name.indexOf("Max") > -1) {
        numberIcon = (
            <Grid container direction="row" alignItems="center">
                <Grid sx={{ marginRight: "3.5px", paddingLeft: "39px" }}>
                    {DbetThirteenIcon(50, 59)}
                </Grid>
                {DbetThirteenLadder(25, 20)}
            </Grid>
        );
    }

    if (round.name.indexOf("Mix") > -1) {
        numberIcon = (
            <Grid container direction="row" alignItems="center">
                <Grid sx={{ marginRight: "3.5px", paddingLeft: "30px" }}>
                    {DbetTwelveIcon(50, 59)}
                </Grid>
                {DbetTwelveLadder(25, 20)}
            </Grid>
        );
    }

    return (
        <>
            <Grid container direction="column" sx={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #000000",
                borderRadius: "20px",
                padding: "20px",
                width: "946px"
            }}>
                {/* Syndicate Tile */}
                <Grid container flexWrap="nowrap" direction='row' alignItems="center" sx={{
                    width: "906px",
                    height: "134px",
                    borderRadius: "20px",
                    marginBottom: "26px",
                    textDecoration: 'none',
                    color: 'inherit'
                }}>

                    <Grid container direction='column' sx={{
                        width: "175px",
                        backgroundImage: gradient,
                        backgroundSize: 'auto 100%',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        height: "150px",
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderTopLeftRadius: "20px",
                        borderBottomLeftRadius: "20px"
                    }}>
                        <Grid item sx={{
                            width: "100%",
                            height: "auto",
                            backgroundSize: 'cover',
                            marginLeft: "0px",
                            marginRight: "0px",
                        }}>

                            <Grid direction="column" container alignItems="center">
                                <Grid container direction="column" alignItems="center"
                                    sx={{
                                        backgroundSize: "contain",
                                        backgroundRepeat: "no-repeat",
                                        padding: "20px"
                                    }}
                                >
                                    {round.name.indexOf("Hockey9") === -1 &&
                                        <Typography sx={{
                                            fontSize: "20px",
                                            fontFamily: "Righteous",
                                            fontWeight: "400",
                                            textTransform: "uppercase",
                                            textAlign: "center",
                                            letterSpacing: "0%",
                                            color: "#F4C004"
                                        }}>
                                            Super
                                        </Typography>
                                    }
                                    {numberIcon}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Grid container direction="column" sx={{
                        width: "731px",
                        height: "150px",
                        flexWrap: "nowrap",
                        backgroundColor: "#112F7C",
                        borderTopRightRadius: "20px",
                        borderBottomRightRadius: "20px",
                        paddingLeft: "30px",
                        paddingRight: "50px"
                    }}>
                        <Grid container direction="row" alignItems="center" justifyContent="flex-start" sx={{paddingTop:"15px", paddingBottom:"10px"}}>
                            <Grid container sx={{                                
                                height: "auto",

                            }} direction='column' alignItems='flex-start'>
                                <Typography sx={smallText}>{t('jackpot')}</Typography>
                                <Typography sx={{...smallTitleUpper, fontSize: "30px", paddingLeft: "0px"}}>{nf2.format(jackpot)}</Typography>
                            </Grid>
                        </Grid>
                        <Grid container direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: "nowrap" }}>

                            <Grid container sx={{
                                width: "280px",
                                height: "auto",
                                overflow: "hidden"
                            }} direction='column' alignItems='flex-start'>
                                <Typography sx={smallText}>{dateString}</Typography>
                                <Typography sx={{
                                    ...smallTitleUpper,
                                    paddingLeft: "0px",
                                    paddingRight: "0px",
                                    width: "100%",
                                    textAlign: "left",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis",
                                    overflow: "hidden"
                                }}>{syndicateName}</Typography>
                            </Grid>

                            <Grid container direction="row" alignItems="center" justifyContent="space-between"
                                sx={{ width: "336px" }}>

                                <Grid container sx={{ width: "100px" }} direction='column' alignItems='center' justifyContent='center'>
                                    <Typography sx={smallText}>{t('shares')}</Typography>
                                    {syndicate.nSharesHidden ?
                                        <Typography sx={smallTitleUpper}>{syndicate.targetNShares}</Typography>
                                        :
                                        <Typography sx={smallTitleUpper}>{'(' + syndicate.nShares + '/' + syndicate.targetNShares + ')'}</Typography>
                                    }
                                </Grid>
                                <Grid sx={{ height: "50px", borderLeft: "4px solid #254BA4" }} />

                                <Grid container sx={{ width: "186px" }} direction='column' alignItems='center' justifyContent='center'>
                                    <Typography sx={stakeStyle}>{formattedStakePerShare}</Typography>
                                </Grid>

                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Syndicate Coupon */}
                {detailedRound && (
                    <Grid sx={{ width: "906px" }}>
                        <TerminalSyndicateCoupon syndicate={syndicate} round={detailedRound} />
                    </Grid>
                )}
            </Grid>
        </>
    );

}

export default TerminalSyndicateCarousel;
